import { FormEvent, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Btn from '../components/ui/Btn';
import { LogoLockup } from '../components/ui/Logo';
import { ApiError, api } from '../lib/api';
import { TokenPair, UserSummary, useAuthStore } from '../stores/auth';

type LoginResponse = {
  user: UserSummary;
  mfa_required: boolean;
  tokens: TokenPair | null;
  challenge_token: string | null;
};

type MfaResponse = TokenPair;

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuthStore((s) => s.setSession);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState<string | null>(null);
  const [pendingUser, setPendingUser] = useState<UserSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const target = (location.state as { from?: string } | null)?.from ?? '/dossiers';

  const onSubmitLogin = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const resp = await api.post<LoginResponse>(
        '/auth/login',
        { email, password },
        { withAuth: false }
      );
      if (resp.mfa_required) {
        setChallenge(resp.challenge_token);
        setPendingUser(resp.user);
      } else if (resp.tokens) {
        setSession(resp.user, resp.tokens);
        navigate(target, { replace: true });
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.problem.detail ?? err.problem.title);
      } else {
        setError('Erreur réseau. Vérifie que l’API est joignable.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitMfa = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const resp = await api.post<MfaResponse>(
        '/auth/mfa/verify',
        { challenge_token: challenge, code },
        { withAuth: false }
      );
      if (pendingUser) {
        setSession(pendingUser, resp);
        navigate(target, { replace: true });
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.problem.detail ?? err.problem.title);
      } else {
        setError('Erreur réseau.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex justify-center">
          <LogoLockup size={36} />
        </div>

        {challenge ? (
          <form
            onSubmit={onSubmitMfa}
            className="space-y-4 rounded-lg border border-rule bg-paper-elevated p-6 shadow-sm"
          >
            <h2 className="font-serif text-lg">Code TOTP</h2>
            <p className="text-sm text-text-secondary">
              Entre le code à 6 chiffres de ton application d’authentification.
            </p>
            <input
              type="text"
              autoFocus
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              className="w-full rounded-md border border-rule-strong bg-paper-elevated px-3 py-2 text-center text-lg tracking-widest font-mono"
              placeholder="000000"
            />
            {error && <p className="text-sm text-cat-def">{error}</p>}
            <Btn
              type="submit"
              variant="primary"
              disabled={submitting || code.length !== 6}
              className="w-full"
            >
              {submitting ? 'Vérification…' : 'Valider'}
            </Btn>
          </form>
        ) : (
          <form
            onSubmit={onSubmitLogin}
            className="space-y-4 rounded-lg border border-rule bg-paper-elevated p-6 shadow-sm"
          >
            <label className="block">
              <span className="block text-[11px] font-medium uppercase tracking-[1px] text-text-secondary">
                Email
              </span>
              <input
                type="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1.5 w-full rounded-md border border-rule-strong bg-paper-elevated px-3 py-2 text-sm focus:border-accent focus:outline-none"
                placeholder="m.toller@m24-avocats.be"
              />
            </label>
            <label className="block">
              <span className="block text-[11px] font-medium uppercase tracking-[1px] text-text-secondary">
                Mot de passe
              </span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1.5 w-full rounded-md border border-rule-strong bg-paper-elevated px-3 py-2 text-sm focus:border-accent focus:outline-none"
              />
            </label>
            {error && <p className="text-sm text-cat-def">{error}</p>}
            <Btn
              type="submit"
              variant="primary"
              disabled={submitting || !email || !password}
              className="w-full"
            >
              {submitting ? 'Connexion…' : 'Se connecter'}
            </Btn>
          </form>
        )}
      </div>
    </div>
  );
}
